/**
 * DOM Error Prevention Utilities
 *
 * This module provides defensive patches for DOM manipulation methods to prevent
 * crashes caused by browser extensions (like Google Translate) that modify the DOM
 * outside of React's control.
 *
 * The NotFoundError "Failed to execute 'removeChild' on 'Node'" occurs when:
 * 1. Google Translate wraps text nodes with <font> tags
 * 2. Browser extensions modify DOM structure
 * 3. React tries to remove nodes that have been moved or wrapped
 */

/**
 * Apply defensive patches to DOM methods to prevent NotFoundError crashes
 * This is specifically for handling Google Translate and similar browser extensions
 * that modify the DOM structure outside of React's control
 */
export function applyDOMErrorPrevention() {
    if (typeof window === "undefined") return;

    // Check if patches have already been applied
    if ((window as any).__domErrorPreventionApplied) return;

    // Store original methods
    const originalRemoveChild = Node.prototype.removeChild;
    const originalInsertBefore = Node.prototype.insertBefore;
    const originalAppendChild = Node.prototype.appendChild;

    // Patch removeChild to handle NotFoundError gracefully
    Node.prototype.removeChild = function(child) {
        if (!child) return child;

        try {
            // Check if the child is actually a child of this node
            if (child.parentNode === this) {
                return originalRemoveChild.call(this, child);
            }

            /*
             * Child is not actually our child, possibly due to DOM manipulation
             * by Google Translate or other extensions
             */
            console.warn("Attempted to remove a node that is not a child of this node. This is likely due to browser extensions modifying the DOM.");
            return child;
        } catch (error: any) {
            // Handle NotFoundError specifically
            if (error.name === "NotFoundError") {
                console.warn("NotFoundError caught in removeChild. This is likely due to Google Translate or browser extensions.", {
                    parent: this,
                    child: child,
                    error: error,
                });
                return child;
            }
            // Re-throw other errors
            throw error;
        }
    };

    // Patch insertBefore to handle NotFoundError gracefully
    Node.prototype.insertBefore = function(newNode, referenceNode) {
        if (!newNode) return newNode;

        try {
            // If referenceNode is provided, check if it's actually our child
            if (referenceNode && referenceNode.parentNode !== this) {
                console.warn("Reference node is not a child of this node. This is likely due to browser extensions modifying the DOM.");
                // Append to the end instead
                return originalAppendChild.call(this, newNode);
            }

            return originalInsertBefore.call(this, newNode, referenceNode);
        } catch (error: any) {
            // Handle NotFoundError specifically
            if (error.name === "NotFoundError") {
                console.warn("NotFoundError caught in insertBefore. This is likely due to Google Translate or browser extensions.", {
                    parent: this,
                    newNode: newNode,
                    referenceNode: referenceNode,
                    error: error,
                });
                // Try to append to the end as a fallback
                try {
                    return originalAppendChild.call(this, newNode);
                } catch (appendError) {
                    console.error("Failed to append node as fallback", appendError);
                    return newNode;
                }
            }
            // Re-throw other errors
            throw error;
        }
    };

    // Mark that patches have been applied
    (window as any).__domErrorPreventionApplied = true;

    // Only log in development
    if (import.meta.env.DEV) {
        console.log("DOM error prevention patches applied successfully");
    }
}

/**
 * Detect if Google Translate is active on the page
 */
export function isGoogleTranslateActive(): boolean {
    if (typeof window === "undefined") return false;

    // Check for Google Translate specific elements
    const hasGoogleTranslateElement = !!(
        document.querySelector(".goog-te-menu-frame") ||
    document.querySelector("#goog-gt-tt") ||
    document.querySelector(".goog-te-banner-frame")
    );

    // Check for <font> tags which Google Translate uses to wrap text
    const hasFontTags = document.getElementsByTagName("font").length > 0;

    // Check if the HTML element has Google Translate classes
    const htmlElement = document.documentElement;
    const hasTranslateClass = htmlElement.classList.contains("translated-ltr") ||
                            htmlElement.classList.contains("translated-rtl");

    return hasGoogleTranslateElement || hasFontTags || hasTranslateClass;
}

/**
 * Add meta tag to prevent Google Translate from offering translation
 * This can help prevent the DOM manipulation issues
 */
export function preventGoogleTranslate() {
    if (typeof window === "undefined") return;

    // Check if meta tag already exists
    const existingMeta = document.querySelector("meta[name=\"google\"]");
    if (existingMeta) return;

    // Create and add meta tag
    const meta = document.createElement("meta");
    meta.name = "google";
    meta.content = "notranslate";
    document.head.appendChild(meta);

    // Also add class to HTML element
    document.documentElement.classList.add("notranslate");
}

/**
 * Prevent Google Maps API initialization errors on pages that don't need Maps
 * This addresses the "undefined is not an object (evaluating 'new za.jD')" error
 * that occurs when Maps API is loaded but not properly initialized
 */
export function preventGoogleMapsErrors() {
    if (typeof window === "undefined") return;

    // Check if Maps error prevention has already been applied
    if ((window as any).__mapsErrorPreventionApplied) return;

    // Prevent Maps API from trying to initialize on pages that don't need it
    const currentPath = window.location.pathname;
    const pathsWithoutMaps = ["/load-fuel", "/loot-shop", "/account", "/collectibles", "/go-vip-dashboard", "/go-vip", "/play", "/cockpit-v3"];

    if (pathsWithoutMaps.some(path => currentPath.includes(path))) {
    // Override google.maps initialization if it tries to load
        const originalGoogle = (window as any).google;
        if (originalGoogle?.maps) {
            // Create a safe stub to prevent initialization errors
            const safeMapsStub = {
                ...originalGoogle.maps,
                Map: function() {
                    console.warn("Google Maps initialization prevented on page that does not need Maps:", currentPath);
                    return {
                        setCenter: () => {},
                        setZoom: () => {},
                        panTo: () => {},
                        addListener: () => {},
                        getCenter: () => ({ lat: () => 0, lng: () => 0 }),
                        getZoom: () => 1,
                    };
                },
            };

            (window as any).google = {
                ...originalGoogle,
                maps: safeMapsStub,
            };
        }
    }

    // Global error handler for Maps API errors
    window.addEventListener("unhandledrejection", (event) => {
        const error = event.reason;
        if (error && typeof error === "object" &&
        (error.message?.includes("za.jD") ||
         error.message?.includes("google.maps") ||
         error.stack?.includes("maps-api-v3"))) {
            console.warn("Google Maps API error caught and suppressed on non-Maps page:", {
                path: window.location.pathname,
                error: error.message,
            });
            event.preventDefault();
        }
    });

    // Mark that Maps error prevention has been applied
    (window as any).__mapsErrorPreventionApplied = true;

    // Only log in development
    if (import.meta.env.DEV) {
        console.log("Google Maps error prevention applied for path:", currentPath);
    }
}